/**
 * Location Intelligence Page
 * Interactive heatmap dashboard with area list, detail panels, and alert management
 */

import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { MapPin, Bell, Loader2 } from 'lucide-react';
import MetricCard from '../components/dashboard/MetricCard';
import DateRangeFilter from '../components/dashboard/DateRangeFilter';
import HeatmapContainer from '../components/dashboard/HeatmapContainer';
import LocationFilterPanel from '../components/dashboard/LocationFilterPanel';
import AreaListPanel from '../components/dashboard/AreaListPanel';
import AreaDetailPanel from '../components/dashboard/AreaDetailPanel';
import AlertConfigDrawer from '../components/dashboard/AlertConfigDrawer';
import RefreshButton from '../components/shared/RefreshButton';
import useDateRange from '../hooks/useDateRange';
import { sentimentToPercent } from '../utils/formatters';
import { getHeatmapData, getLocationSummary, getLocationAreas } from '../services/locationService';

const LocationHeatmapPage = () => {
  const { from, to, onChange } = useDateRange();

  // Data
  const [summary, setSummary] = useState(null);
  const [prevSummary, setPrevSummary] = useState(null);
  const [heatmapPoints, setHeatmapPoints] = useState([]);
  const [areas, setAreas] = useState([]);

  // Loading
  const [loading, setLoading] = useState(true);

  // Filters
  const [typeFilter, setTypeFilter] = useState('');
  const [sentimentFilter, setSentimentFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [areaSort, setAreaSort] = useState('conversations');

  // Selection & UI
  const [selectedArea, setSelectedArea] = useState(null);
  const [showAlertConfig, setShowAlertConfig] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: from, end_date: to };
      if (typeFilter) params.type = typeFilter;
      if (sentimentFilter) params.sentiment = sentimentFilter;
      if (urgencyFilter) params.urgency = urgencyFilter;

      const [heatmapRes, summaryRes, areasRes] = await Promise.all([
        getHeatmapData(params),
        getLocationSummary({ start_date: from, end_date: to }),
        getLocationAreas({ start_date: from, end_date: to }),
      ]);

      if (heatmapRes.success) setHeatmapPoints(heatmapRes.data);
      else toast.error(heatmapRes.error);

      if (summaryRes.success) setSummary(summaryRes.data);
      else toast.error(summaryRes.error);

      if (areasRes.success) setAreas(areasRes.data);
      else toast.error(areasRes.error);

      // Fetch previous period for trend comparison
      const rangeDays = Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000));
      const prevFrom = new Date(new Date(from).getTime() - rangeDays * 86400000).toISOString().split('T')[0];
      const prevRes = await getLocationSummary({ start_date: prevFrom, end_date: from });
      if (prevRes.success) setPrevSummary(prevRes.data);
      else setPrevSummary(null);
    } catch {
      toast.error('Failed to load location data');
    } finally {
      setLoading(false);
    }
  }, [from, to, typeFilter, sentimentFilter, urgencyFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAreaSelect = (city) => {
    setSelectedArea(selectedArea === city ? null : city);
  };

  return (
    <div className="space-y-4">
      {/* Controls bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <RefreshButton onClick={fetchData} loading={loading} />
          <LocationFilterPanel
            typeFilter={typeFilter}
            sentimentFilter={sentimentFilter}
            urgencyFilter={urgencyFilter}
            onTypeChange={setTypeFilter}
            onSentimentChange={setSentimentFilter}
            onUrgencyChange={setUrgencyFilter}
          />
        </div>
        <div className="flex items-center space-x-3">
          <DateRangeFilter from={from} to={to} onChange={onChange} />
          <button
            onClick={() => setShowAlertConfig(true)}
            className="flex items-center space-x-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
          >
            <Bell size={14} />
            <span>Alerts</span>
            {summary?.active_alerts > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 ml-1">
                {summary.active_alerts}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="animate-spin text-primary-500" size={20} />
          <span className="ml-2 text-sm text-gray-500">Loading location data...</span>
        </div>
      )}

      {/* Metrics row */}
      {summary && (() => {
        const pctChange = (curr, prev) => prev > 0 ? Math.round((curr - prev) / prev * 100) : null;
        const locatedChange = prevSummary ? pctChange(summary.total_located, prevSummary.total_located) : undefined;
        const alertsChange = prevSummary ? pctChange(summary.active_alerts, prevSummary.active_alerts) : undefined;
        return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard
            label="Located Conversations"
            value={summary.total_located}
            change={locatedChange}
            changeLabel="vs prev period"
            color="#3b82f6"
          />
          <MetricCard
            label="Top City"
            value={summary.top_city || 'N/A'}
            color="#8b5cf6"
          />
          <MetricCard
            label="Avg Sentiment"
            value={summary.avg_sentiment_score != null
              ? sentimentToPercent(summary.avg_sentiment_score) + '%'
              : 'N/A'}
            color={summary.avg_sentiment_score == null ? '#9ca3af'
              : sentimentToPercent(summary.avg_sentiment_score) >= 50 ? '#22c55e' : '#ef4444'}
          />
          <MetricCard
            label="Active Alerts"
            value={summary.active_alerts}
            change={alertsChange}
            changeLabel="vs prev period"
            color={summary.active_alerts > 0 ? '#ef4444' : '#6b7280'}
          />
        </div>
        );
      })()}

      {/* Map + area list split */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="card overflow-hidden">
            <HeatmapContainer data={heatmapPoints} height={480} onCityClick={handleAreaSelect} />
          </div>
        </div>
        <div className="lg:col-span-1">
          <AreaListPanel
            areas={areas}
            selectedArea={selectedArea}
            areaSort={areaSort}
            onSelectArea={handleAreaSelect}
            onSortChange={setAreaSort}
          />
        </div>
      </div>

      {/* Area detail drawer */}
      {selectedArea && (
        <AreaDetailPanel
          city={selectedArea}
          startDate={from}
          endDate={to}
          onClose={() => setSelectedArea(null)}
          onOpenAlertConfig={() => setShowAlertConfig(true)}
        />
      )}

      {/* Alert config drawer */}
      <AlertConfigDrawer
        isOpen={showAlertConfig}
        onClose={() => setShowAlertConfig(false)}
        onRulesChanged={fetchData}
      />
    </div>
  );
};

export default LocationHeatmapPage;
